import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Footer } from "../components/shop/Footer";
import { LAST_UPDATED } from "../data/policies";

const Section = ({ section, index, slug }) => (
  <div className="mt-10" data-testid={`${slug}-section-${index}`}>
    <h2 className="font-serif-display font-medium text-2xl text-[#2B1F1B]">{section.heading}</h2>
    {(section.paragraphs || []).map((p) => (
      <p key={p} className="mt-3 text-base font-light leading-relaxed text-[#4A3C34]">{p}</p>
    ))}
    {section.bullets && (
      <ul className="mt-4 space-y-2.5">
        {section.bullets.map((b) => (
          <li key={b} className="flex items-start gap-3 text-base font-light leading-relaxed text-[#4A3C34]">
            <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[#C9A45C] shrink-0" aria-hidden />
            {b}
          </li>
        ))}
      </ul>
    )}
  </div>
);

export default function PolicyPage({ policy }) {
  const slug = policy.route.slice(1);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `${policy.title} — CELUSTA`;
    return () => { document.title = "CELUSTA | Leave-In Conditioner"; };
  }, [policy]);

  return (
    <div className="bg-[#F8F4EC] min-h-screen flex flex-col" data-testid={`policy-page-${slug}`}>
      <header className="border-b border-[rgba(58,42,37,0.08)] bg-[#F8F4EC]">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="font-serif-display text-2xl tracking-[0.25em] text-[#3A2A25] font-medium" data-testid="policy-brand-logo">
            CELUSTA
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs font-mono-accent uppercase tracking-[0.18em] text-[#756A64] hover:text-[#3A2A25] transition-colors duration-300"
            data-testid="policy-back-link"
          >
            <ArrowLeft size={14} strokeWidth={2} />
            Back to Shop
          </Link>
        </div>
      </header>

      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-6 sm:px-10 py-16 sm:py-24">
          <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#C9A45C] mb-4">CELUSTA</p>
          <h1 className="font-serif-display font-medium text-4xl sm:text-5xl tracking-tight text-[#2B1F1B] leading-tight" data-testid={`${slug}-title`}>
            {policy.title}
          </h1>
          <p className="mt-4 font-mono-accent text-[11px] uppercase tracking-[0.2em] text-[#9A8D85]" data-testid={`${slug}-last-updated`}>
            Last updated: {LAST_UPDATED}
          </p>
          <p className="mt-6 text-base font-light leading-relaxed text-[#4A3C34]">{policy.intro}</p>

          {policy.sections.map((s, i) => (
            <Section key={s.heading} section={s} index={i} slug={slug} />
          ))}

          <div className="mt-14 pt-8 border-t border-[rgba(58,42,37,0.1)]">
            <p className="text-sm font-light text-[#756A64]">
              Questions? We're here to help —{" "}
              <a href="mailto:info@celusta.com" className="underline underline-offset-4 decoration-[#C9A45C]/60 text-[#3A2A25] hover:text-[#C9A45C] transition-colors duration-300" data-testid={`${slug}-contact-email`}>
                info@celusta.com
              </a>
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
